import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API_BASE } from "@/lib/rentApi";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Download, Trash2, ShieldCheck, FileText, UserCog, Eye } from "lucide-react";
import { toast } from "sonner";

export default function DataPrivacy() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const exportData = async () => {
    try {
      const token = localStorage.getItem("rent_token");
      const res = await fetch(`${API_BASE}/gdpr/export`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "rent_mes_donnees.json"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Vos données ont été exportées");
    } catch { toast.error("Export impossible"); }
  };

  const deleteAccount = async () => {
    setBusy(true);
    try {
      await api.delete("/gdpr/account");
      toast.success("Compte et données supprimés");
      logout();
      navigate("/");
    } catch { toast.error("Suppression impossible"); setBusy(false); }
  };

  const rights = [
    [Eye, "Droit d'accès", "Consultez à tout moment l'ensemble des données que nous détenons sur vous."],
    [Download, "Droit à la portabilité", "Exportez vos données dans un format lisible (JSON)."],
    [UserCog, "Droit de rectification", "Corrigez vos informations depuis votre profil et vos fiches."],
    [Trash2, "Droit à l'effacement", "Supprimez définitivement votre compte et vos données associées."],
  ];

  return (
    <div>
      <PageHeader title="Mes données (RGPD)" subtitle="Gérez vos données personnelles et exercez vos droits." />

      <div className="p-5 rounded-xl border border-border bg-card mb-6 flex items-center gap-4">
        <ShieldCheck className="w-8 h-8 text-[hsl(var(--chart-2))]" />
        <div>
          <p className="font-medium">Consentement enregistré</p>
          <p className="text-sm text-muted-foreground">Compte {user?.email} — conforme au Règlement Général sur la Protection des Données.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        {rights.map(([Icon, title, desc], i) => (
          <div key={i} className="p-5 rounded-xl border border-border bg-card">
            <Icon className="w-5 h-5 text-[hsl(var(--ring))]" />
            <h3 className="font-heading font-semibold mt-3">{title}</h3>
            <p className="text-sm text-muted-foreground mt-1">{desc}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3">
        <Button className="rounded-full gap-2" onClick={exportData} data-testid="export-data-btn">
          <Download className="w-4 h-4" /> Exporter mes données
        </Button>
        <a href="/confidentialite" target="_blank" rel="noreferrer">
          <Button variant="outline" className="rounded-full gap-2" data-testid="privacy-policy-link"><FileText className="w-4 h-4" /> Politique de confidentialité</Button>
        </a>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="rounded-full gap-2 text-destructive border-destructive/40" data-testid="delete-account-btn">
              <Trash2 className="w-4 h-4" /> Supprimer mon compte
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer définitivement votre compte ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible. Toutes vos données (biens, baux, documents, écritures, loyers) seront
                effacées conformément à votre droit à l'effacement.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-delete">Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={deleteAccount} disabled={busy} className="bg-destructive text-destructive-foreground" data-testid="confirm-delete">
                Supprimer définitivement
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
